import { toast } from 'react-toastify';

/**
 * Определяет, является ли ошибка таймаутом
 */
export const isTimeoutError = (error: unknown): boolean => {
  if (!error) return false;

  // Проверка для axios ошибок
  if (typeof error === 'object' && 'code' in error) {
    const code = (error as any).code;
    // ECONNABORTED - стандартный код таймаута в axios
    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
      return true;
    }
  }

  // Проверка по статус коду HTTP
  if (typeof error === 'object' && 'response' in error) {
    const status = (error as any).response?.status;
    // 408 - Request Timeout, 504 - Gateway Timeout
    if (status === 408 || status === 504) {
      return true;
    }
  }

  // Проверка по сообщению об ошибке
  const errorMessage = getErrorMessage(error);
  if (errorMessage && /timeout|timed out|time out/i.test(errorMessage)) {
    return true;
  }

  // Проверка по наличию свойства timeout
  if (typeof error === 'object' && 'timeout' in error) {
    return true;
  }

  return false;
};

/**
 * Извлекает сообщение об ошибке из объекта ошибки
 */
export const getErrorMessage = (error: unknown): string => {
  if (!error) return 'An unexpected error occurred';

  // Axios ошибки
  if (typeof error === 'object' && 'response' in error) {
    const axiosError = error as any;
    const responseData = axiosError.response?.data;

    // Попытка извлечь сообщение из разных форматов ответа
    if (responseData) {
      if (typeof responseData === 'string') {
        return responseData;
      }
      if (responseData.message) {
        return responseData.message;
      }
      if (responseData.error) {
        return responseData.error;
      }
      if (responseData.detail) {
        // Для валидационных ошибок FastAPI
        if (Array.isArray(responseData.detail)) {
          const firstError = responseData.detail[0];
          return firstError?.msg || firstError?.message || String(responseData.detail[0]);
        }
        return typeof responseData.detail === 'string' ? responseData.detail : String(responseData.detail);
      }
      if (responseData.errors && Array.isArray(responseData.errors)) {
        const firstError = responseData.errors[0];
        return firstError?.message || String(firstError);
      }
    }

    // Статус код ошибки
    const status = axiosError.response?.status;
    if (status) {
      return `Request failed with status ${status}`;
    }
  }

  // Обычные Error объекты
  if (error instanceof Error) {
    return error.message;
  }

  // Строковые ошибки
  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
};

/**
 * Типы ошибок для стандартных сообщений
 */
export enum ErrorType {
  FORM = 'FORM',
  UNDERWRITING = 'UNDERWRITING',
  UNDERWRITING_TIMEOUT = 'UNDERWRITING_TIMEOUT',
  ADDRESS_SSN = 'ADDRESS_SSN',
  CONTACT = 'CONTACT',
  APPLICATION_SUBMIT = 'APPLICATION_SUBMIT',
}

/**
 * Получает стандартное сообщение об ошибке по типу
 */
export const getStandardErrorMessage = (errorType: ErrorType): string => {
  switch (errorType) {
    case ErrorType.FORM:
      return 'Failed to save form data. Please try again.';
    case ErrorType.UNDERWRITING:
      return 'Failed to process your application. Please try again later.';
    case ErrorType.UNDERWRITING_TIMEOUT:
      return 'Request timed out. Please check your connection and try again.';
    case ErrorType.ADDRESS_SSN:
      return 'Failed to update address information. Please try again.';
    case ErrorType.CONTACT:
      return 'Failed to update contact information. Please try again.';
    case ErrorType.APPLICATION_SUBMIT:
      return 'Failed to submit application. Please try again.';
    default:
      return 'An error occurred. Please try again.';
  }
};

/**
 * Показывает тост с ошибкой
 */
export const showErrorToast = (errorType: ErrorType, error?: unknown): void => {
  const message = getStandardErrorMessage(errorType);
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Обрабатывает ошибку и показывает соответствующий тост
 */
/**
 * Checks if the error is a connection/network error
 */
export const isConnectionError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  if ('code' in error) {
    const code = (error as any).code;
    if (code === 'ERR_NETWORK' || code === 'ERR_INTERNET_DISCONNECTED') {
      return true;
    }
  }

  if ('message' in error && (error as any).message === 'Network Error') {
    return true;
  }

  if (!('response' in error) || (error as any).response == null) {
    return true;
  }

  return false;
};

/**
 * Returns an appropriate error message for update failures
 */
export const getUpdateErrorMessage = (error: unknown): string => {
  if (isConnectionError(error)) {
    return 'We are sorry but form cannot be updated. Please check your internet connection.';
  }
  return 'We are sorry but form cannot be updated. Please try later.';
};

export const handleError = (errorType: ErrorType, error?: unknown): void => {
  // Определяем, является ли это таймаутом
  const isTimeout = isTimeoutError(error);

  // Для андеррайтинга используем специальный тип для таймаутов
  if (errorType === ErrorType.UNDERWRITING && isTimeout) {
    showErrorToast(ErrorType.UNDERWRITING_TIMEOUT, error);
  } else {
    showErrorToast(errorType, error);
  }
};
